import { create } from 'zustand';
import { notificationsApi } from '@/lib/api/notifications';
import type { AppNotification } from '@/types/api';

interface NotificationStoreState {
  initialized: boolean;
  loading: boolean;
  notifications: AppNotification[];
  unreadCount: number;

  initialize: () => Promise<void>;
  teardown: () => void;
  refresh: () => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  markRead: (id: number | string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export const useNotificationStore = create<NotificationStoreState>((set, get) => ({
  initialized: false,
  loading: false,
  notifications: [],
  unreadCount: 0,

  initialize: async () => {
    if (get().initialized) return;
    set({ initialized: true });
    await Promise.all([get().refresh(), get().refreshUnreadCount()]);
  },

  teardown: () => {
    set({ initialized: false, notifications: [], unreadCount: 0 });
  },

  refresh: async () => {
    set({ loading: true });
    try {
      const notifications = await notificationsApi.list();
      set({ notifications });
    } catch {
      /* silent — badge/list just won't update this cycle */
    } finally {
      set({ loading: false });
    }
  },

  refreshUnreadCount: async () => {
    try {
      const unreadCount = await notificationsApi.unreadCount();
      set({ unreadCount });
    } catch {
      /* silent */
    }
  },

  markRead: async (id) => {
    const target = get().notifications.find((n) => n.id === id);
    if (!target || target.read) return;
    set((s) => ({
      notifications: s.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
      unreadCount: Math.max(0, s.unreadCount - 1),
    }));
    try {
      await notificationsApi.markRead(id);
    } catch {
      await get().refresh();
      await get().refreshUnreadCount();
    }
  },

  markAllRead: async () => {
    const hadUnread = get().notifications.some((n) => !n.read);
    if (!hadUnread && get().unreadCount === 0) return;
    set((s) => ({
      notifications: s.notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }));
    try {
      await notificationsApi.markAllRead();
    } catch {
      await get().refresh();
      await get().refreshUnreadCount();
    }
  },
}));
